import React, { useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useNotification } from '../../context/NotificationContext';
import { useIntegrationsStatusContext } from '../../context/IntegrationsStatusContext';
import type { Integration } from '../../services/integration.types';
import PageLayout from '../common/PageLayout';
import IntegrationsOverview from './IntegrationsOverview';
import IntegrationSelectPage from './IntegrationSelectPage';
import IntegrationCreatePage from './IntegrationCreatePage';
import IntegrationDetailPage from './IntegrationDetailPage';
import { useIntegrationMutations } from './hooks/useIntegrationMutations';
import { useStepValidation } from '../../hooks/useStepValidation';
import { useUnifiedAccountState } from '../../hooks/useUnifiedAccountState';
import Breadcrumb from '../common/Breadcrumb';

const IntegrationsMain: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { integrations, isLoading, syncIntegration, isSyncing } = useIntegrationsStatusContext();
    const [deletingIntegrationName, setDeletingIntegrationName] = useState<string | null>(null);

    const {
        deleteIntegration,
        isExchangeCreating,
        isWalletCreating,
        isCsvImporting,
    } = useIntegrationMutations();

    const { validateStep1ToStep2 } = useStepValidation();

    const handleSync = (integration: Integration) => {
        integration.status.status = 'in_progress';
        syncIntegration(
            { 
                jobId: integration.jobId, 
                platformName: integration.platformName,
                name: integration.name
            },
            {
                onSuccess: () => {},
                onError: () => {
                    showNotification(t('integrations.overview.notifications.syncError'), 'error');
                },
            }
        );
    };

    const handleDelete = async (integration: Integration) => {
        setDeletingIntegrationName(integration.name);
        
        const optimisticUpdate = setTimeout(() => {}, 500);
        
        deleteIntegration(
            {
                name: integration.name,
                jobId: integration.jobId,
                jobTableName: integration.jobTableName,
            },
            {
                onSuccess: () => {
                    clearTimeout(optimisticUpdate);
                    setDeletingIntegrationName(null);
                },
                onError: () => {
                    clearTimeout(optimisticUpdate);
                    setDeletingIntegrationName(null);
                    showNotification(t('integrations.overview.notifications.deleteError'), 'error');
                },
            }
        );
    };

    const showSkeleton = isExchangeCreating || isWalletCreating || isCsvImporting || isSyncing || isLoading;
    const stepValidation = validateStep1ToStep2();
    const accountState = useUnifiedAccountState();
    
    const handleGuidanceAction = () => {
        if (accountState.guidance?.actionRoute) {
            navigate(accountState.guidance.actionRoute);
        }
    };

    return (
        <PageLayout>
            <IntegrationsOverview
                integrations={integrations}
                onSync={handleSync}
                onDelete={handleDelete}
                deletingIntegrationName={deletingIntegrationName}
                isLoading={showSkeleton}
            />
        </PageLayout>
    );
};

const Integrations: React.FC = () => {
    return (
        <Routes>
            <Route index element={<IntegrationsMain />} />
            <Route path="add" element={<IntegrationSelectPage />} />
            <Route path="add/:platformName" element={<IntegrationCreatePage />} />
            <Route path=":integrationId" element={<IntegrationDetailPage />} />
        </Routes>
    );
};

export default Integrations;
